import { MouseEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Alert,
  Button,
  CssBaseline,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { BoardInput, CollageInput, GameInput, Space, SpaceType, WallInput } from '../../mainApi';
import { useMainStore } from '../../stores/mainStore';
import { useSpacesStore } from '../../stores/spacesStore';
import { useWorkspacesStore } from '../../stores/workspacesStore';
import { WorkspaceUtils } from '../../utils';
import { MainContentHeader } from './MainContentHeader';
import { MainSidebar } from './MainSidebar';
import { MainTopBar } from './MainTopBar';
import { SpacesGrid } from '../spaces/SpacesGrid';
import { SpaceMenu } from '../spaces/SpaceMenu';
import { SpaceThumbnailDialog } from '../spaces/SpaceThumbnailDialog';
import { TemplatesList, TemplatesTab } from '../templates/TemplatesList';
import { UserThemeDialog } from '../settings/UserThemeDialog';
import { FeedbackDialog } from '../support/FeedbackDialog';
import { HelpDialog, HelpDialogTabKey } from '../support/HelpDialog';
import { ExternalKeysDialog } from '../settings/ExternalKeysDialog';
import { DotvotingDialog } from '../spaces/DotvotingDialog';
import { GameDialog } from '../spaces/GameDialog';
import { WhiteboardDialog } from '../spaces/WhiteboardDialog';
import { WorkspaceControls } from '../workspaces/WorkspaceControls';
import { WorkspaceLinkBanner } from '../workspaces/WorkspaceLinkBanner';
import { TrialNotificationDialog } from '../notifications/TrialNotificationDialog';
import { QuickSortNotificationDialog } from '../notifications/QuickSortNotificationDialog';
import { ActiveSpaceLimitDialog } from '../notifications/ActiveSpaceLimitDialog';
import { useAuthStore } from '../../stores/authStore';
import { sendFeedback } from '../../services/feedback.service';
import { createQuickSortExampleGame, requestEmailVerification, updateUserNotifications } from '../../services/user.service';
import { isActiveSpaceLimitError } from '../../services/activeSpaceLimit.service';
import { hasReachedActiveOwnedSpaceLimit } from '../../utils/activeSpaceLimit.utils';
import {
  getTrial,
  setTrialEndedSeen,
  setTrialNotificationSeen,
  setTrialNotified5DaysSeen,
} from '../../services/trial.service';

const drawerWidth = 360;
type TrialNotificationVariant = 'initial' | 'fiveDay' | 'ended';

export const Main = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const hasInitializedWorkspaceFromQuery = useRef(false);

  const { user, logout, setMe } = useAuthStore();

  const {
    orderBy,
    filter,
    selectedWorkspaceId,
    setOrderBy,
    selectHome,
    selectArchived,
    selectTemplates,
    selectWorkspace,
  } = useMainStore();

  const {
    workspaces,
    isSavingWorkspaces,
    loadWorkspaces,
    addWorkspace,
    editWorkspace,
    removeWorkspace,
  } = useWorkspacesStore();

  const {
    spaces,
    isLoadingSpaces,
    isSavingSpaces,
    spaceDialog,
    spaceForm,
    selectedSpaceForMenu,
    loadSpaces,
    addSpace,
    editSpace,
    copySpace,
    archiveSpace,
    removeSpace,
    setThumbnail,
    openSpaceDialog,
    closeSpaceDialog,
    setSpaceMenuSpace,
  } = useSpacesStore();

  const [spaceMenuAnchor, setSpaceMenuAnchor] = useState<null | HTMLElement>(null);
  const [verificationNotice, setVerificationNotice] = useState('');
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [helpDialogInitialTab, setHelpDialogInitialTab] = useState<HelpDialogTabKey>('board');
  const [isExternalKeysDialogOpen, setIsExternalKeysDialogOpen] = useState(false);
  const [isTrialDialogOpen, setIsTrialDialogOpen] = useState(false);
  const [isQuickSortNotificationDialogOpen, setIsQuickSortNotificationDialogOpen] = useState(false);
  const [isCreatingQuickSortExampleGame, setIsCreatingQuickSortExampleGame] = useState(false);
  const [quickSortExampleGameError, setQuickSortExampleGameError] = useState<string | null>(null);
  const [isActiveSpaceLimitDialogOpen, setIsActiveSpaceLimitDialogOpen] = useState(false);
  const [thumbnailDialogSpace, setThumbnailDialogSpace] = useState<Space | null>(null);
  const [trialNotification, setTrialNotification] = useState<{
    variant: TrialNotificationVariant | null;
    daysRemaining?: number;
  }>({ variant: null });

  const isArchivedRoute = pathname.endsWith('/archived');
  const isTemplatesRoute = pathname.endsWith('/templates');
  const workspaceIdFromQuery = useMemo(
    () => new URLSearchParams(search).get('workspaceId')?.trim() ?? '',
    [search]
  );
  const activeTemplateTab = useMemo<TemplatesTab>(() => {
    const tab = new URLSearchParams(search).get('tab');

    return tab === 'marketplace' ? 'marketplace' : 'my';
  }, [search]);

  const withWorkspaceQuery = (path: string, workspaceId?: string | null) => {
    const params = new URLSearchParams(search);
    const resolvedWorkspaceId =
      typeof workspaceId === 'string' ? workspaceId.trim() : workspaceIdFromQuery;

    if (resolvedWorkspaceId) {
      params.set('workspaceId', resolvedWorkspaceId);
    } else {
      params.delete('workspaceId');
    }

    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
  };

  const handleTemplateTabChange = (_event: SyntheticEvent, nextTab: TemplatesTab) => {
    const params = new URLSearchParams(search);

    if (nextTab === 'marketplace') {
      params.set('tab', 'marketplace');
    } else {
      params.delete('tab');
    }

    const queryString = params.toString();
    navigate(queryString ? `/templates?${queryString}` : '/templates', { replace: true });
  };

  useEffect(() => {
    void loadWorkspaces();
    void loadSpaces();
  }, [loadWorkspaces, loadSpaces]);

  useEffect(() => {
    if (hasInitializedWorkspaceFromQuery.current) {
      return;
    }

    hasInitializedWorkspaceFromQuery.current = true;
    if (workspaceIdFromQuery) {
      selectWorkspace(workspaceIdFromQuery);
    }
  }, [selectWorkspace, workspaceIdFromQuery]);

  useEffect(() => {
    if (isTemplatesRoute && filter !== 'templates') {
      selectTemplates();
      return;
    }

    if (isArchivedRoute && filter !== 'archived') {
      selectArchived();
      return;
    }

    if (
      !isArchivedRoute &&
      !isTemplatesRoute &&
      (filter === 'archived' || filter === 'templates')
    ) {
      selectHome();
    }
  }, [filter, isArchivedRoute, isTemplatesRoute, selectArchived, selectHome, selectTemplates]);

  useEffect(() => {
    let isActive = true;

    const loadTrialNotification = async () => {
      const trial = await getTrial();

      if (
        !user ||
        !trial ||
        (user.accountLevel !== 0 && user.accountLevel !== 99) ||
        trial.notifiedTrialEnded
      ) {
        if (isActive) {
          setIsTrialDialogOpen(false);
          setTrialNotification({ variant: null });
        }
        return;
      }

      try {
        if (!isActive) {
          return;
        }

        if (user.accountLevel === 0 && !trial.notifiedTrialEnded) {
          setTrialNotification({ variant: 'ended' });
          setIsTrialDialogOpen(true);
          return;
        } else if (user.subscribedUntil) {
          const millisecondsRemaining = new Date(user.subscribedUntil).getTime() - Date.now();
          const signedDaysRemaining = Math.ceil(millisecondsRemaining / (24 * 60 * 60 * 1000));

          if (user.hasValidTrial) {
            if (!trial.notificationSeen) {
              setTrialNotification({ variant: 'initial' });
              setIsTrialDialogOpen(true);
              return;
            }

            if (signedDaysRemaining <= 5 && signedDaysRemaining >= 0 && !trial.notified5Days) {
              setTrialNotification({
                variant: 'fiveDay',
                daysRemaining: signedDaysRemaining,
              });
              setIsTrialDialogOpen(true);
              return;
            }
          }
        }

        setIsTrialDialogOpen(false);
        setTrialNotification({ variant: null });
      } catch {
        if (isActive) {
          setIsTrialDialogOpen(false);
          setTrialNotification({ variant: null });
        }
      }
    };

    void loadTrialNotification();

    return () => {
      isActive = false;
    };
  }, [user]);

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ?? null,
    [workspaces, selectedWorkspaceId]
  );

  const sectionTitle =
    filter === 'archived'
      ? 'Archived'
      : filter === 'templates'
      ? 'Templates'
      : filter === 'workspace'
      ? selectedWorkspace?.title || 'Workspace'
      : 'Home';
  const headerLabel =
    filter === 'templates' ? (
      <Tabs
        value={activeTemplateTab}
        onChange={handleTemplateTabChange}
        sx={{
          minHeight: 40,
          '.MuiTabs-indicator': {
            height: 3,
            borderRadius: 999,
          },
        }}
      >
        <Tab
          value="my"
          label="My templates"
          sx={{ fontSize: 20, fontWeight: 700, textTransform: 'none', minHeight: 40 }}
        />
        <Tab
          value="marketplace"
          label="Marketplace"
          sx={{ fontSize: 20, fontWeight: 700, textTransform: 'none', minHeight: 40 }}
        />
      </Tabs>
    ) : (
      sectionTitle
    );
  const showSpacesHeaderLoader = isSavingSpaces || (isLoadingSpaces && spaces.length > 0);

  const hasReachedActiveSpaceLimit = useMemo(
    () => hasReachedActiveOwnedSpaceLimit(user, spaces),
    [spaces, user]
  );

  const launchSpaceDialog = (
    mode: 'create' | 'edit' | 'copy',
    spaceType: SpaceType,
    space: Space | null
  ) => {
    if ((mode === 'create' || mode === 'copy') && hasReachedActiveSpaceLimit) {
      setSpaceMenuAnchor(null);
      setSpaceMenuSpace(null);
      setIsActiveSpaceLimitDialogOpen(true);
      return;
    }

    openSpaceDialog({
      mode,
      spaceType,
      space,
      initialForm: {
        title:
          mode === 'copy' && space?.title
            ? `Copy of ${String(space.title)}`
            : (space?.title as string | undefined) ?? '',
        workspaceTitle:
          WorkspaceUtils.workspaceNameFromSpace(space, workspaces, selectedWorkspace) ??
          (selectedWorkspace?.title as string | undefined) ??
          '',
        description: (space?.description as string | undefined) ?? '',
        password: '',
      },
    });
    setSpaceMenuAnchor(null);
  };

  const submitSpacePayload = async (payload: BoardInput | WallInput | CollageInput | GameInput) => {
    try {
      if (spaceDialog.mode === 'create') {
        const path = await addSpace(spaceDialog.spaceType, payload);
        closeSpaceDialog();
        if (path) {
          window.location.href = withWorkspaceQuery(path);
        }
        return;
      }

      if (spaceDialog.space) {
        if (spaceDialog.mode === 'edit') {
          await editSpace(spaceDialog.space, payload);
        } else {
          await copySpace(spaceDialog.space, payload);
        }
      }

      closeSpaceDialog();
    } catch (error) {
      if (isActiveSpaceLimitError(error)) {
        closeSpaceDialog();
        setSpaceMenuAnchor(null);
        setSpaceMenuSpace(null);
        await loadSpaces();
        setIsActiveSpaceLimitDialogOpen(true);
        return;
      }

      throw error;
    }
  };

  const onCopyWorkspaceLink = () => {
    if (!selectedWorkspace?.externalId) {
      return;
    }

    void navigator.clipboard.writeText(
      `https://workspace.dotstorming.com/${selectedWorkspace.externalId}`
    );
  };

  const onSpaceMenuOpen = (event: MouseEvent<HTMLElement>, space: Space) => {
    event.preventDefault();
    event.stopPropagation();
    setSpaceMenuSpace(space);
    setSpaceMenuAnchor(event.currentTarget);
  };

  const closeSpaceMenu = () => {
    setSpaceMenuAnchor(null);
    setSpaceMenuSpace(null);
  };

  const handleArchiveRestore = async () => {
    const space = selectedSpaceForMenu;

    if (!space) {
      return;
    }

    closeSpaceMenu();
    await archiveSpace(space, !space.archived);
  };

  const handleDeleteSpace = async () => {
    const space = selectedSpaceForMenu;

    if (!space) {
      return;
    }

    closeSpaceMenu();
    await removeSpace(space);
  };

  const handleSetThumbnail = async (file: File) => {
    if (!thumbnailDialogSpace) {
      return;
    }

    await setThumbnail(thumbnailDialogSpace, file);
    setThumbnailDialogSpace(null);
    closeSpaceMenu();
  };

  const handleSendFeedback = async (comment: string) => {
    const trimmedComment = comment.trim();

    if (!trimmedComment) {
      return false;
    }

    try {
      await sendFeedback(trimmedComment, 'main');
      return true;
    } catch {
      return false;
    }
  };

  const handleCloseTrialDialog = async () => {
    if (!trialNotification.variant) {
      setIsTrialDialogOpen(false);
      return;
    }

    if (trialNotification.variant === 'initial') {
      await setTrialNotificationSeen();
    } else if (trialNotification.variant === 'fiveDay') {
      await setTrialNotified5DaysSeen();
    } else {
      await setTrialEndedSeen();
    }

    setIsTrialDialogOpen(false);
    setTrialNotification({ variant: null });
  };

  const handleIntroduceQuickSort = async () => {
    if (!user) {
      return;
    }

    try {
      const response = await updateUserNotifications({
        ...(user.notifications ?? {}),
        quickSort: false,
      });

      await setMe({
        ...user,
        ...(response?.user ?? {}),
        notifications: {
          ...(user.notifications ?? {}),
          ...(response?.user?.notifications ?? {}),
          quickSort: false,
        },
      });
    } finally {
      setQuickSortExampleGameError(null);
      setIsQuickSortNotificationDialogOpen(true);
    }
  };

  const handleCreateQuickSortExampleGame = async () => {
    if (hasReachedActiveSpaceLimit) {
      setIsQuickSortNotificationDialogOpen(false);
      setIsActiveSpaceLimitDialogOpen(true);
      return;
    }

    setIsCreatingQuickSortExampleGame(true);
    setQuickSortExampleGameError(null);

    try {
      await createQuickSortExampleGame();
      await loadSpaces();
      setIsQuickSortNotificationDialogOpen(false);
    } catch (error) {
      if (isActiveSpaceLimitError(error)) {
        setIsQuickSortNotificationDialogOpen(false);
        setIsActiveSpaceLimitDialogOpen(true);
        return;
      }

      setQuickSortExampleGameError(
        error instanceof Error ? error.message : 'Unable to create the example Quick Sort game.'
      );
    } finally {
      setIsCreatingQuickSortExampleGame(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        bgcolor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <CssBaseline />
      {user?.emailVerified === false && (
        <Alert
          severity="warning"
          sx={{ position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1500 }}
          action={<Button size="small" onClick={() => {
            void requestEmailVerification()
              .then(() => setVerificationNotice('Verification email sent. Check your inbox.'))
              .catch((error) => setVerificationNotice(error instanceof Error ? error.message : 'Unable to send verification email.'));
          }}>Resend email</Button>}
        >
          {verificationNotice || 'Verify your email before you can share a board, wall or game.'}
        </Alert>
      )}

      <WorkspaceControls
        filter={filter}
        selectedWorkspaceId={selectedWorkspaceId}
        selectedWorkspace={selectedWorkspace}
        isSaving={isSavingWorkspaces || isSavingSpaces}
        addWorkspace={addWorkspace}
        editWorkspace={editWorkspace}
        removeWorkspace={removeWorkspace}
        loadWorkspaces={loadWorkspaces}
        selectHome={selectHome}
      >
        {({
          canOpenWorkspaceMenu,
          memberCount,
          onCreateWorkspace,
          onWorkspaceMenuOpen,
          onWorkspaceMembersOpen,
        }) => (
          <>
            <MainSidebar
              drawerWidth={drawerWidth}
              filter={filter}
              selectedWorkspaceId={selectedWorkspaceId}
              workspaces={workspaces}
              homeHref="/main"
              onSelectHome={() => {
                selectHome();
                navigate('/main');
              }}
              onSelectTemplates={() => {
                selectTemplates();
                navigate('/templates');
              }}
              onSelectArchived={() => {
                selectArchived();
                navigate(withWorkspaceQuery('/archived'));
              }}
              onSelectWorkspace={(workspaceId) => {
                selectWorkspace(workspaceId);
                navigate(withWorkspaceQuery('/main', workspaceId));
              }}
              onCreateWorkspace={onCreateWorkspace}
            />

            <Stack component="main" spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <Stack>
                <MainTopBar
                  headerLabel={headerLabel}
                  profileLabel={
                    user?.name
                      ? user.name
                          .split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part[0]?.toUpperCase())
                          .join('')
                      : ''
                  }
                  accountLevel={user?.accountLevel}
                  canEditTheme={Boolean((user?.accountLevel ?? 0) > 0)}
                  onOpenExternalKeys={() => {
                    setIsExternalKeysDialogOpen(true);
                  }}
                  onOpenFeedback={() => {
                    setIsFeedbackDialogOpen(true);
                  }}
                  onOpenHelp={() => {
                    setHelpDialogInitialTab('board');
                    setIsHelpDialogOpen(true);
                  }}
                  onNavigateProfile={() => {
                    navigate('/profile');
                  }}
                  onNavigateBilling={() => {
                    navigate('/billing');
                  }}
                  onNavigateArchived={() => {
                    selectArchived();
                    navigate(withWorkspaceQuery('/archived'));
                  }}
                  onEditTheme={() => {
                    setIsThemeDialogOpen(true);
                  }}
                  onLogout={() => {
                    logout();
                    navigate('/main');
                  }}
                />
                {showSpacesHeaderLoader ? <LinearProgress /> : null}
              </Stack>
              <Stack
                sx={{
                  flexGrow: 1,
                  m: '0px !important',
                  px: 4,
                  pt: 3,
                  pb: 10,
                  overflow: 'auto',
                  gap: filter === 'templates' ? 0 : 3,
                }}
              >
                <MainContentHeader
                  sectionTitle={sectionTitle}
                  canOpenWorkspaceMenu={canOpenWorkspaceMenu}
                  showOrderBy={filter !== 'templates'}
                  memberCount={memberCount}
                  onWorkspaceMembersOpen={onWorkspaceMembersOpen}
                  orderBy={orderBy}
                  onOrderByChange={setOrderBy}
                  onWorkspaceMenuOpen={onWorkspaceMenuOpen}
                />

                <Box>
                  {filter === 'templates' ? (
                    <TemplatesList activeTab={activeTemplateTab} />
                  ) : (
                    <SpacesGrid
                      onSpaceMenuOpen={onSpaceMenuOpen}
                      onCreateSpace={(spaceType) => launchSpaceDialog('create', spaceType, null)}
                    />
                  )}
                </Box>
              </Stack>
              {selectedWorkspace?.externallyVisible && selectedWorkspace.externalId && (
                <Box
                  sx={{ position: 'sticky', bottom: 16, zIndex: 2 }}
                  style={{ margin: '0px 16px' }}
                >
                  <WorkspaceLinkBanner
                    externalId={selectedWorkspace.externalId}
                    onCopy={onCopyWorkspaceLink}
                  />
                </Box>
              )}
            </Stack>

            <UserThemeDialog open={isThemeDialogOpen} onClose={() => setIsThemeDialogOpen(false)} />
            <ExternalKeysDialog
              open={isExternalKeysDialogOpen}
              onClose={() => setIsExternalKeysDialogOpen(false)}
            />
            <FeedbackDialog
              open={isFeedbackDialogOpen}
              onClose={() => setIsFeedbackDialogOpen(false)}
              onSendFeedback={handleSendFeedback}
            />
            <HelpDialog
              open={isHelpDialogOpen}
              onClose={() => setIsHelpDialogOpen(false)}
              initialTabKey={helpDialogInitialTab}
            />
            <TrialNotificationDialog
              open={isTrialDialogOpen && Boolean(trialNotification.variant)}
              variant={trialNotification.variant}
              daysRemaining={trialNotification.daysRemaining}
              onClose={handleCloseTrialDialog}
              onNavigateBilling={async () => {
                await handleCloseTrialDialog();
                navigate('/billing');
              }}
            />
            <QuickSortNotificationDialog
              open={isQuickSortNotificationDialogOpen}
              onClose={() => setIsQuickSortNotificationDialogOpen(false)}
              onCreateExampleGame={handleCreateQuickSortExampleGame}
              isCreatingExampleGame={isCreatingQuickSortExampleGame}
              createExampleGameError={quickSortExampleGameError}
            />
            <ActiveSpaceLimitDialog
              open={isActiveSpaceLimitDialogOpen}
              onClose={() => setIsActiveSpaceLimitDialogOpen(false)}
              onNavigateBilling={() => navigate('/billing')}
            />
          </>
        )}
      </WorkspaceControls>

      <SpaceMenu
        anchorEl={spaceMenuAnchor}
        space={selectedSpaceForMenu}
        onClose={closeSpaceMenu}
        onEdit={() => {
          if (selectedSpaceForMenu) {
            launchSpaceDialog('edit', selectedSpaceForMenu.type, selectedSpaceForMenu);
          }
        }}
        onSetThumbnail={() => {
          const space = selectedSpaceForMenu;

          if (space) {
            setThumbnailDialogSpace(space);
            closeSpaceMenu();
          }
        }}
        onCopy={() => {
          if (selectedSpaceForMenu) {
            launchSpaceDialog('copy', selectedSpaceForMenu.type, selectedSpaceForMenu);
          }
        }}
        onArchiveRestore={() => void handleArchiveRestore()}
        onDelete={() => void handleDeleteSpace()}
      />

      <SpaceThumbnailDialog
        open={Boolean(thumbnailDialogSpace)}
        space={thumbnailDialogSpace}
        isSaving={isSavingSpaces}
        onClose={() => setThumbnailDialogSpace(null)}
        onSubmit={handleSetThumbnail}
      />

      {spaceDialog.spaceType === 'board' ? (
        <DotvotingDialog
          dialogState={spaceDialog}
          isSaving={isSavingSpaces}
          workspaces={workspaces}
          initialTitle={spaceForm.title}
          initialWorkspaceTitle={spaceForm.workspaceTitle}
          initialDescription={spaceForm.description}
          initialPassword={spaceForm.password}
          onClose={closeSpaceDialog}
          onSubmit={(payload) => void submitSpacePayload(payload)}
        />
      ) : spaceDialog.spaceType === 'game' ? (
        <GameDialog
          dialogState={spaceDialog}
          isSaving={isSavingSpaces}
          workspaces={workspaces}
          initialTitle={spaceForm.title}
          initialWorkspaceTitle={spaceForm.workspaceTitle}
          onClose={closeSpaceDialog}
          onSubmit={(payload) => void submitSpacePayload(payload)}
        />
      ) : (
        <WhiteboardDialog
          dialogState={spaceDialog}
          isSaving={isSavingSpaces}
          workspaces={workspaces}
          initialTitle={spaceForm.title}
          initialWorkspaceTitle={spaceForm.workspaceTitle}
          initialPassword={spaceForm.password}
          onClose={closeSpaceDialog}
          onSubmit={(payload) => void submitSpacePayload(payload)}
        />
      )}

      {Boolean(user && user.notifications?.quickSort !== false) && (
        <Button
          onClick={() => void handleIntroduceQuickSort()}
          variant="text"
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: 288,
            justifyContent: 'flex-start',
            borderRadius: '6px',
            border: '1px solid #d8dde7',
            bgcolor: '#ffffff',
            color: '#022b3a',
            px: 2,
            py: 1.5,
            textAlign: 'left',
            textTransform: 'none',
            boxShadow: '0 10px 30px rgba(2, 43, 58, 0.16)',
            '&:hover': {
              bgcolor: '#f8fafc',
              borderColor: '#c8d0dc',
              boxShadow: '0 12px 34px rgba(2, 43, 58, 0.2)',
            },
          }}
        >
          <Box
            component="img"
            src="https://storage.googleapis.com/dotstorming-img/graphic_game.svg"
            alt=""
            sx={{ width: 48, height: 48, flex: '0 0 auto' }}
          />
          <Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.2, fontWeight: 700, opacity: 0.7 }}>
              New
            </Typography>
            <Typography sx={{ fontSize: 16, lineHeight: 1.25, fontWeight: 800 }}>
              Introducing Quick Sorts
            </Typography>
          </Box>
        </Button>
      )}
    </Box>
  );
};
